import { useMutation, useQuery } from 'urql';
import React from 'react';
import { graphql } from '../../gql/index.ts';
import { UserForm, UserFormSubmitData } from './UserForm.tsx';
import { useNavigate } from '@tanstack/react-router';
import { cloneAndRemoveTypename } from '../../public/util.ts';
import { useKeycloak } from '../../shared/auth/KeycloakProvider.tsx';
import { restApiUrl } from '../../shared/helpers/backendUrl';

interface UserEditPageProps {
  companyId: string;
  userId: string;
}

const UserQuery = graphql(/* GraphQL */ `
  query user($companyId: String!, $userId: String!) {
    user(companyId: $companyId, userId: $userId) {
      privileges
      address {
        firstName
        lastName
        title
        email
      }
      lecturer {
        id
        jobTitle
        vita
        qualification
        pathToProfileImage
        allowSevenDaysReminderForLecturer
        allowOneDayReminderForLecturer
      }
    }
  }
`);

const UpdateUserMutation = graphql(/* GraphQL */ `
  mutation updateUser(
    $companyId: String!
    $userId: String!
    $updatedValues: UpdateUserDto!
  ) {
    updateUser(
      companyId: $companyId
      userId: $userId
      updatedValues: $updatedValues
    )
  }
`);

const DeleteLecturerProfileImageMutation = graphql(/* GraphQL */ `
  mutation deleteLecturerProfileImageForUser(
    $companyId: String!
    $userId: String!
  ) {
    deleteLecturerProfileImageForUser(companyId: $companyId, userId: $userId)
  }
`);

export const UserEditPage: React.FC<UserEditPageProps> = ({
  companyId,
  userId,
}) => {
  // TODO: Error handling
  const [{ data }] = useQuery({
    query: UserQuery,
    variables: { companyId, userId },
    pause: companyId === undefined || companyId === null || !userId,
  });

  const [, updateUser] = useMutation(UpdateUserMutation);
  const [, deleteLecturerProfileImage] = useMutation(
    DeleteLecturerProfileImageMutation,
  );
  const navigate = useNavigate();
  const { token } = useKeycloak();

  const lecturer = data?.user?.lecturer;

  const onFormSubmit = async ({
    formData,
    file,
    fileShouldBeDeleted,
  }: UserFormSubmitData) => {
    if (file && lecturer) {
      const uploadData = new FormData();
      uploadData.append('profile-image', file);

      await fetch(restApiUrl(`/lecturer/${lecturer.id}/profile-image`), {
        method: 'POST',
        body: uploadData,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
    }

    if (fileShouldBeDeleted && !file) {
      await deleteLecturerProfileImage({ companyId, userId });
    }

    // Pass through reminder values so they don't get overwritten
    if (formData.lecturer && lecturer) {
      formData.lecturer.allowSevenDaysReminderForLecturer =
        lecturer.allowSevenDaysReminderForLecturer ?? true;
      formData.lecturer.allowOneDayReminderForLecturer =
        lecturer.allowOneDayReminderForLecturer ?? true;
    }

    await updateUser({
      companyId,
      userId,
      updatedValues: formData,
    });

    navigate({ to: '/admin/users' });
  };

  const userValue = data?.user
    ? (() => {
        const { lecturer: _lecturer, ...rest } = cloneAndRemoveTypename(
          data.user,
        );
        return rest;
      })()
    : undefined;

  return (
    <UserForm
      value={userValue}
      lecturer={lecturer ? cloneAndRemoveTypename(lecturer) : undefined}
      onSubmit={onFormSubmit}
    />
  );
};
